import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../firebase.init";
import {
  Copy,
  Download,
  FileText,
  Hash,
  Type,
  AlignLeft,
  Tag,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const ProductDescriptionGenerator = () => {
  // State management
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [productName, setProductName] = useState("");
  const [productDetails, setProductDetails] = useState("");
  const [language, setLanguage] = useState("English");

  // Generated content sections
  const [productDescription, setProductDescription] = useState("");
  const [featureHighlights, setFeatureHighlights] = useState([]);
  const [specificationDetails, setSpecificationDetails] = useState([]);
  const [seoOptimizedContent, setSeoOptimizedContent] = useState({
    titleOptions: [],
    seoBulletPoints: [],
    seoDescription: "",
  });
  const [metaTags, setMetaTags] = useState({
    title: "",
    description: "",
  });
  const [slug, setSlug] = useState("");
  const [productTags, setProductTags] = useState([]);

  // User data
  const [credits, setCredits] = useState(0);

  // API keys state
  const [apiKeys, setApiKeys] = useState([]);
  
  // Language options - Expanded to 120+ languages
  const languageOptions = [
    { value: "English", label: "English" },
    { value: "Spanish", label: "Spanish" },
    { value: "French", label: "French" },
    { value: "German", label: "German" },
    { value: "Chinese", label: "Chinese" },
    { value: "Japanese", label: "Japanese" },
    { value: "Arabic", label: "العربية (Arabic)" },
    { value: "Hindi", label: "हिन्दी (Hindi)" },
    { value: "Portuguese", label: "Português (Portuguese)" },
    { value: "Russian", label: "Русский (Russian)" },
    { value: "Bengali", label: "বাংলা (Bengali)" },
    { value: "Indonesian", label: "Bahasa Indonesia (Indonesian)" },
    { value: "Urdu", label: "اردو (Urdu)" },
    { value: "Italian", label: "Italiano (Italian)" },
    { value: "Turkish", label: "Türkçe (Turkish)" },
    { value: "Korean", label: "한국어 (Korean)" },
    { value: "Vietnamese", label: "Tiếng Việt (Vietnamese)" },
    { value: "Polish", label: "Polski (Polish)" },
    { value: "Dutch", label: "Nederlands (Dutch)" },
    { value: "Thai", label: "ไทย (Thai)" },
    { value: "Persian", label: "فارسی (Persian)" },
    { value: "Romanian", label: "Română (Romanian)" },
    { value: "Greek", label: "Ελληνικά (Greek)" },
    { value: "Czech", label: "Čeština (Czech)" },
    { value: "Swedish", label: "Svenska (Swedish)" },
    { value: "Hungarian", label: "Magyar (Hungarian)" },
    { value: "Danish", label: "Dansk (Danish)" },
    { value: "Finnish", label: "Suomi (Finnish)" },
    { value: "Hebrew", label: "עברית (Hebrew)" },
    { value: "Norwegian", label: "Norsk (Norwegian)" },
    { value: "Ukrainian", label: "Українська (Ukrainian)" },
    { value: "Malay", label: "Bahasa Melayu (Malay)" },
    { value: "Filipino", label: "Filipino" },
    { value: "Swahili", label: "Kiswahili (Swahili)" },
    { value: "Tamil", label: "தமிழ் (Tamil)" },
    { value: "Telugu", label: "తెలుగు (Telugu)" },
    { value: "Marathi", label: "मराठी (Marathi)" },
    { value: "Gujarati", label: "ગુજરાતી (Gujarati)" },
    { value: "Kannada", label: "ಕನ್ನಡ (Kannada)" },
    { value: "Punjabi", label: "ਪੰਜਾਬੀ (Punjabi)" },
    { value: "Burmese", label: "မြန်မာစာ (Burmese)" },
    { value: "Pashto", label: "پښتو (Pashto)" },
    { value: "Amharic", label: "አማርኛ (Amharic)" },
    { value: "Azerbaijani", label: "Azərbaycanca (Azerbaijani)" },
    { value: "Belarusian", label: "Беларуская (Belarusian)" },
    { value: "Bulgarian", label: "Български (Bulgarian)" },
    { value: "Catalan", label: "Català (Catalan)" },
    { value: "Croatian", label: "Hrvatski (Croatian)" },
    { value: "Estonian", label: "Eesti (Estonian)" },
    { value: "Georgian", label: "ქართული (Georgian)" },
    { value: "Icelandic", label: "Íslenska (Icelandic)" },
    { value: "Irish", label: "Gaeilge (Irish)" },
    { value: "Khmer", label: "ភាសាខ្មែរ (Khmer)" },
    { value: "Lao", label: "ພາສາລາວ (Lao)" },
    { value: "Latvian", label: "Latviešu (Latvian)" },
    { value: "Lithuanian", label: "Lietuvių (Lithuanian)" },
    { value: "Macedonian", label: "Македонски (Macedonian)" },
    { value: "Malayalam", label: "മലയാളം (Malayalam)" },
    { value: "Mongolian", label: "Монгол (Mongolian)" },
    { value: "Nepali", label: "नेपाली (Nepali)" },
    { value: "Sinhala", label: "සිංහල (Sinhala)" },
    { value: "Slovak", label: "Slovenčina (Slovak)" },
    { value: "Slovenian", label: "Slovenščina (Slovenian)" },
    { value: "Albanian", label: "Shqip (Albanian)" },
    { value: "Serbian", label: "Српски (Serbian)" },
    { value: "Somali", label: "Soomaali (Somali)" },
    { value: "Sundanese", label: "Basa Sunda (Sundanese)" },
    { value: "Tajik", label: "Тоҷикӣ (Tajik)" },
    { value: "Uzbek", label: "Oʻzbekcha (Uzbek)" },
    { value: "Yoruba", label: "Yorùbá (Yoruba)" },
    { value: "Zulu", label: "isiZulu (Zulu)" },
    { value: "Afrikaans", label: "Afrikaans" },
    { value: "Akan", label: "Akan" },
    { value: "Assamese", label: "অসমীয়া (Assamese)" },
    { value: "Aymara", label: "Aymar aru (Aymara)" },
    { value: "Bambara", label: "Bamanankan (Bambara)" },
    { value: "Bashkir", label: "Башҡортса (Bashkir)" },
    { value: "Bhojpuri", label: "भोजपुरी (Bhojpuri)" },
    { value: "Chichewa", label: "Chicheŵa (Chichewa)" },
    { value: "Chuvash", label: "Чӑвашла (Chuvash)" },
    { value: "Cornish", label: "Kernewek (Cornish)" },
    { value: "Dhivehi", label: "ދިވެހިބަސް (Dhivehi)" },
    { value: "Dogri", label: "डोगरी (Dogri)" },
    { value: "Esperanto", label: "Esperanto" },
    { value: "Fijian", label: "Vosa Vakaviti (Fijian)" },
    { value: "Frisian", label: "Frysk (Frisian)" },
    { value: "Galician", label: "Galego (Galician)" },
    { value: "Hausa", label: "Hausa (Hausa)" },
    { value: "Hawaiian", label: "ʻŌlelo Hawaiʻi (Hawaiian)" },
    { value: "Hmong", label: "Hmoob (Hmong)" },
    { value: "Igbo", label: "Igbo (Igbo)" },
    { value: "Javanese", label: "Basa Jawa (Javanese)" },
    { value: "Kannada", label: "ಕನ್ನಡ (Kannada)" },
    { value: "Kazakh", label: "Қазақ тілі (Kazakh)" },
    { value: "Kurdish", label: "Kurdî (Kurdish)" },
    { value: "Kyrgyz", label: "Кыргызча (Kyrgyz)" },
    { value: "Luxembourgish", label: "Lëtzebuergesch (Luxembourgish)" },
    { value: "Malagasy", label: "Malagasy" },
    { value: "Maltese", label: "Malti (Maltese)" },
    { value: "Maori", label: "Te Reo Māori (Maori)" },
    { value: "Manipuri", label: "মৈতৈলোন্ (Manipuri)" },
    { value: "Mizo", label: "Mizo ṭawng (Mizo)" },
    { value: "Navajo", label: "Diné bizaad (Navajo)" },
    { value: "Odia", label: "ଓଡ଼ିଆ (Odia)" },
    { value: "Papiamento", label: "Papiamento" },
    { value: "Quechua", label: "Runa Simi (Quechua)" },
    { value: "Samoan", label: "Gagana Samoa (Samoan)" },
    { value: "Scots Gaelic", label: "Gàidhlig (Scots Gaelic)" },
    { value: "Sesotho", label: "Sesotho (Sesotho)" },
    { value: "Shona", label: "ChiShona (Shona)" },
    { value: "Sindhi", label: "سنڌي (Sindhi)" },
    { value: "Tahitian", label: "Reo Tahiti (Tahitian)" },
    { value: "Tatar", label: "Татар теле (Tatar)" },
    { value: "Tibetan", label: "བོད་ཡིག (Tibetan)" },
    { value: "Tigrinya", label: "ትግርኛ (Tigrinya)" },
    { value: "Tsonga", label: "Xitsonga (Tsonga)" },
    { value: "Turkmen", label: "Türkmençe (Turkmen)" },
    { value: "Uyghur", label: "ئۇيغۇرچە (Uyghur)" },
    { value: "Welsh", label: "Cymraeg (Welsh)" },
    { value: "Xhosa", label: "isiXhosa (Xhosa)" },
  ];

  // Clean text by removing markdown symbols
  const cleanText = (text) => {
    return text
      .replace(/\*/g, "") // Remove asterisks
      .replace(/#{1,6}\s?/g, "") // Remove markdown headers
      .replace(/`{1,3}/g, "") // Remove code blocks
      .replace(/\[.*?\]\(.*?\)/g, "") // Remove markdown links
      .replace(/!\[.*?\]\(.*?\)/g, "") // Remove markdown images
      .trim();
  };

  // Fetch API keys from endpoint
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) {
          setError("No API keys available. Please contact administrator.");
        }
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Get random API key
  const getRandomApiKey = () => {
    if (apiKeys.length === 0) {
      throw new Error("No API keys available");
    }
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Fetch user data and API keys when user is authenticated
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://ecommerce-product-05-19692c57dcd1.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          setCredits(userData.currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Generate meta tags as a fallback
  const generateMetaTags = async (
    productName,
    productDescription,
    language
  ) => {
    try {
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;

      const prompt = `Generate SEO-optimized and reader-focused meta tags for the following product:

Product Name: ${productName}
Product Description: ${productDescription}
Language: ${language}

Please provide:
1. Meta Title (60 characters max): Create a compelling title that includes primary keywords and attracts clicks.
2. Meta Description (160 characters max): Write a reader-focused description that highlights key benefits, includes relevant keywords naturally, and encourages clicks. Make it engaging and informative.

Format your response as:
META TITLE: [title]
META DESCRIPTION: [description]`;

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [
                {
                  text: prompt,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 256,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        const generatedText = data.candidates[0].content.parts[0].text;

        // Parse the response
        const titleMatch = generatedText.match(/META TITLE:\s*(.+)/);
        const descMatch = generatedText.match(/META DESCRIPTION:\s*(.+)/);

        return {
          title: titleMatch ? cleanText(titleMatch[1]) : "",
          description: descMatch ? cleanText(descMatch[1]) : "",
        };
      } else {
        throw new Error("No content generated");
      }
    } catch (error) {
      console.error("Error generating meta tags:", error);
      return { title: "", description: "" };
    }
  };

  // Construct the AI prompt
  const constructPrompt = () => {
    return `You are an AI-powered SEO expert specializing in e-commerce product descriptions. 
    Your task is to create a complete SEO-optimized product content package based on the provided information.
    
    Product Name: ${productName}
    Product Details: ${productDetails}
    Language: ${language}
    
    Please generate the following sections:
    
    1. Product Descriptions:
       Write a compelling, keyword-rich description of the product. Keep it engaging, benefit-driven, and conversion-focused.
       Format the description in paragraphs. When you have two or more sentences that form a complete thought, start a new paragraph.
       Do not use any markdown symbols like asterisks (*), hashtags (#), or other formatting.
    
    2. Feature Highlights:
       Provide 5-7 bullet points highlighting the product's main features and benefits.
       Do not use asterisks (*) or any other markdown symbols in the bullet points.
    
    3. Specification Details:
       List detailed technical or physical specifications in bullet points or a clean structured format.
       Do not use asterisks (*) or any other markdown symbols in the specifications.
    
    4. SEO-Optimized Content:
       a. Optimized Titles: Suggest 3 optimized titles for the product.
       b. SEO Bullet Points: Provide 5-7 bullet points optimized for search engines.
       c. SEO Description: Write a product description optimized for search engines.
       Do not use asterisks (*) or any other markdown symbols in any of these elements.
    
    5. Meta Tags:
       a. Meta Title: Provide a recommended meta title (60 characters max). Make it compelling and include primary keywords.
       b. Meta Description: Provide a reader-focused and SEO-friendly meta description (160 characters max). 
          - Provide a reader-focused, SEO-friendly meta description (maximum 160 characters).
          - Emphasize **why someone should choose this product** by highlighting its unique benefits, features, or advantages.
          - Naturally include main keywords.
          - Make it engaging, informative, and click-worthy for potential customers.
    
    6. Slug:
       Suggest an SEO-friendly slug for the product URL.
    
    7. Products Tags:
       Provide 5-7 relevant tags for the product, separated by commas.
    
    Format your response with clear section headers as follows:
    
    ===PRODUCT DESCRIPTIONS===
    [Content]
    
    ===FEATURE HIGHLIGHTS===
    - [Bullet point 1]
    - [Bullet point 2]
    ...
    
    ===SPECIFICATION DETAILS===
    - [Specification 1]
    - [Specification 2]
    ...
    
    ===SEO-OPTIMIZED CONTENT===
    ===OPTIMIZED TITLES===
    [Title 1]
    [Title 2]
    [Title 3]
    
    ===SEO BULLET POINTS===
    - [Bullet point 1]
    - [Bullet point 2]
    ...
    
    ===SEO DESCRIPTION===
    [Content]
    
    ===META TAGS===
    ===META TITLE===
    [Content]
    
    ===META DESCRIPTION===
    [Content]
    
    ===SLUG===
    [Content]
    
    ===PRODUCTS TAGS===
    [Tag 1], [Tag 2], [Tag 3], ...`;
  };

  // Parse the AI response
  const parseResponse = (responseText) => {
    const sections = responseText.split("===");
    const result = {};

    for (let i = 1; i < sections.length; i += 2) {
      const sectionName = sections[i] ? sections[i].trim() : "";
      const sectionContent = sections[i + 1] ? sections[i + 1].trim() : "";

      if (sectionName === "PRODUCT DESCRIPTIONS") {
        // Clean the product description and format as paragraphs
        const cleanedContent = cleanText(sectionContent);
        // Split by double newlines to identify paragraphs
        const paragraphs = cleanedContent
          .split(/\n\s*\n/)
          .filter((p) => p.trim() !== "");
        result.productDescription = paragraphs.join("\n\n");
      } else if (sectionName === "FEATURE HIGHLIGHTS") {
        result.featureHighlights = sectionContent
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line) => cleanText(line.replace(/^-\s*/, "")));
      } else if (sectionName === "SPECIFICATION DETAILS") {
        result.specificationDetails = sectionContent
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line) => cleanText(line.replace(/^-\s*/, "")));
      } else if (sectionName === "SEO-OPTIMIZED CONTENT") {
        // Parse nested sections
        const nestedSections = sectionContent.split("===");
        const seoContent = {
          titleOptions: [],
          seoBulletPoints: [],
          seoDescription: "",
        };

        for (let j = 1; j < nestedSections.length; j += 2) {
          const nestedName = nestedSections[j] ? nestedSections[j].trim() : "";
          const nestedContent = nestedSections[j + 1]
            ? nestedSections[j + 1].trim()
            : "";

          if (nestedName === "OPTIMIZED TITLES") {
            seoContent.titleOptions = nestedContent
              .split("\n")
              .filter((line) => line.trim() !== "")
              .map((line) => cleanText(line));
          } else if (nestedName === "SEO BULLET POINTS") {
            seoContent.seoBulletPoints = nestedContent
              .split("\n")
              .filter((line) => line.trim() !== "")
              .map((line) => cleanText(line.replace(/^-\s*/, "")));
          } else if (nestedName === "SEO DESCRIPTION") {
            seoContent.seoDescription = cleanText(nestedContent);
          }
        }

        result.seoOptimizedContent = seoContent;
      } else if (sectionName === "META TAGS") {
        // Parse nested sections using a more robust approach
        const metaTags = {
          title: "",
          description: "",
        };

        // Split the META TAGS section into nested sections
        const metaSections = sectionContent.split("===");

        // Iterate through nested sections in pairs (header, content)
        for (let j = 1; j < metaSections.length; j += 2) {
          const metaSectionName = metaSections[j] ? metaSections[j].trim() : "";
          const metaSectionContent = metaSections[j + 1]
            ? metaSections[j + 1].trim()
            : "";

          if (metaSectionName === "META TITLE") {
            metaTags.title = cleanText(metaSectionContent);
          } else if (metaSectionName === "META DESCRIPTION") {
            metaTags.description = cleanText(metaSectionContent);
          }
        }

        result.metaTags = metaTags;
      } else if (sectionName === "SLUG") {
        result.slug = cleanText(sectionContent);
      } else if (sectionName === "PRODUCTS TAGS") {
        // Parse comma-separated tags
        const tagsText = cleanText(sectionContent);
        result.productTags = tagsText
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag !== "");
      }
    }

    return result;
  };

  // Generate a product description package
  const generateDescription = async () => {
    if (!productName.trim() || !productDetails.trim()) {
      setError("Product name and details are required");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }
    if (credits < 1) {
      setError(
        "Insufficient credits. You need at least 1 credit to generate a product description."
      );
      return;
    }
    setGenerating(true);
    setError("");

    // Reset previous results
    setProductDescription("");
    setFeatureHighlights([]);
    setSpecificationDetails([]);
    setSeoOptimizedContent({
      titleOptions: [],
      seoBulletPoints: [],
      seoDescription: "",
    });
    setMetaTags({
      title: "",
      description: "",
    });
    setSlug("");
    setProductTags([]);

    try {
      // Deduct 1 credit
      const deductResponse = await axios.post(
        `https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: 1,
        }
      );
      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }
      // Update credits locally
      setCredits((prev) => prev - 1);

      // Generate description
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [
                {
                  text: prompt,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2048,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        const generatedText = data.candidates[0].content.parts[0].text;
        const parsedContent = parseResponse(generatedText);

        // Check if meta tags were generated properly, if not, generate them separately
        if (
          !parsedContent.metaTags ||
          !parsedContent.metaTags.title ||
          !parsedContent.metaTags.description
        ) {
          const fallbackMetaTags = await generateMetaTags(
            productName,
            parsedContent.productDescription || productDetails,
            language
          );

          if (fallbackMetaTags.title || fallbackMetaTags.description) {
            parsedContent.metaTags = {
              title: fallbackMetaTags.title || "",
              description: fallbackMetaTags.description || "",
            };
          }
        }

        // Update state with parsed content
        if (parsedContent.productDescription) {
          setProductDescription(parsedContent.productDescription);
        }

        if (
          parsedContent.featureHighlights &&
          parsedContent.featureHighlights.length > 0
        ) {
          setFeatureHighlights(parsedContent.featureHighlights);
        }

        if (
          parsedContent.specificationDetails &&
          parsedContent.specificationDetails.length > 0
        ) {
          setSpecificationDetails(parsedContent.specificationDetails);
        }

        if (parsedContent.seoOptimizedContent) {
          setSeoOptimizedContent(parsedContent.seoOptimizedContent);
        }

        if (parsedContent.metaTags) {
          setMetaTags(parsedContent.metaTags);
        }

        if (parsedContent.slug) {
          setSlug(parsedContent.slug);
        }

        if (parsedContent.productTags) {
          setProductTags(parsedContent.productTags);
        }

        toast.success("Product content package generated successfully!");
      } else {
        throw new Error("No content was generated from the API response");
      }
    } catch (err) {
      console.error("Error generating description:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);

      // Refund credits on failure
      try {
        await axios.post(`https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: 1,
        });
        setCredits((prev) => prev + 1);
      } catch (refundErr) {
        console.error("Error refunding credits:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  // Copy text to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`${type} copied to clipboard!`);
      })
      .catch((err) => {
        toast.error(`Failed to copy ${type.toLowerCase()} to clipboard`);
        console.error("Error copying text: ", err);
      });
  };

  // Download content as a text file
  const downloadContent = (content, filename) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded successfully!`);
  };

  // Download all content as a package
  const downloadAllContent = () => {
    let allContent = `PRODUCT DESCRIPTION PACKAGE\n\n`;
    allContent += `Product Name: ${productName}\n\n`;

    if (productDescription) {
      allContent += `===PRODUCT DESCRIPTION===\n${productDescription}\n\n`;
    }

    if (featureHighlights && featureHighlights.length > 0) {
      allContent += `===FEATURE HIGHLIGHTS===\n`;
      featureHighlights.forEach((highlight) => {
        allContent += `• ${highlight}\n`;
      });
      allContent += "\n";
    }

    if (specificationDetails && specificationDetails.length > 0) {
      allContent += `===SPECIFICATION DETAILS===\n`;
      specificationDetails.forEach((spec) => {
        allContent += `• ${spec}\n`;
      });
      allContent += "\n";
    }

    if (seoOptimizedContent) {
      if (
        seoOptimizedContent.titleOptions &&
        seoOptimizedContent.titleOptions.length > 0
      ) {
        allContent += `===SEO OPTIMIZED TITLES===\n`;
        seoOptimizedContent.titleOptions.forEach((title) => {
          allContent += `${title}\n`;
        });
        allContent += "\n";
      }

      if (
        seoOptimizedContent.seoBulletPoints &&
        seoOptimizedContent.seoBulletPoints.length > 0
      ) {
        allContent += `===SEO BULLET POINTS===\n`;
        seoOptimizedContent.seoBulletPoints.forEach((point) => {
          allContent += `• ${point}\n`;
        });
        allContent += "\n";
      }

      if (seoOptimizedContent.seoDescription) {
        allContent += `===SEO DESCRIPTION===\n${seoOptimizedContent.seoDescription}\n\n`;
      }
    }

    if (metaTags) {
      if (metaTags.title) {
        allContent += `===META TITLE===\n${metaTags.title}\n\n`;
      }

      if (metaTags.description) {
        allContent += `===META DESCRIPTION===\n${metaTags.description}\n\n`;
      }
    }

    if (slug) {
      allContent += `===SLUG===\n${slug}\n\n`;
    }

    if (productTags && productTags.length > 0) {
      allContent += `===PRODUCTS TAGS===\n`;
      productTags.forEach((tag) => {
        allContent += `#${tag}\n`;
      });
      allContent += "\n";
    }

    downloadContent(
      allContent,
      `${productName.replace(/\s+/g, "-")}-product-content.txt`
    );
  };

  // Save the generated content
  const saveContent = () => {
    if (!productDescription && featureHighlights.length === 0) {
      toast.error("No content to save");
      return;
    }
    // Here you would typically save to your database
    toast.success("Content saved successfully!");
    // For demo purposes, we'll just show a success message
  };

  // Reset form
  const resetForm = () => {
    setProductName("");
    setProductDetails("");
    setLanguage("English");
    setProductDescription("");
    setFeatureHighlights([]);
    setSpecificationDetails([]);
    setSeoOptimizedContent({
      titleOptions: [],
      seoBulletPoints: [],
      seoDescription: "",
    });
    setMetaTags({
      title: "",
      description: "",
    });
    setSlug("");
    setProductTags([]);
    setError("");
  };

  // Buy more credits
  const buyMoreCredits = () => {
    toast("Redirecting to payment page...");
    window.location.href = "/user/select-package";
  };

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
        <Toaster position="top-right" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Please log in to use the Product Description Generator</h2>
          <button
            className="login-btn"
            onClick={() => (window.location.href = "/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-description-generator">
      {/* Top Navigation */}
      <div className="top-navigation">
        <button className="back-btn" onClick={() => window.history.back()}>
          <i className="bi bi-arrow-left"></i> Back
        </button>
        <div className="credits-display">
          <div className="credits-info">
            <span className="credits-label">Credits:</span>
            <span className="credits-value">{credits}</span>
          </div>
          {credits < 5 && (
            <button className="buy-credits-btn" onClick={buyMoreCredits}>
              Buy More
            </button>
          )}
        </div>
      </div>

      {/* Section Title */}
      <div className="section-header">
        <h1 className="section-title">
          Product Description
          <span
            className="info-icon"
            title="AI-powered SEO expert for e-commerce"
          >
            <i className="bi bi-info-circle"></i>
          </span>
        </h1>
        <p className="section-subtext">
          This is your AI-powered SEO expert, creating captivating and
          keyword-rich e-commerce product descriptions to boost your online
          sales.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Main Content */}
      <div className="main-content">
        {/* Left Side - Form Area */}
        <div className="form-section">
          <div className="form-step">
            <h3 className="step-title">Step 1 – Name of the Product</h3>
            <div className="form-group">
              <input
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="e.g., iPhone 12, Samsung Galaxy Note 20, etc."
                className="form-control"
              />
            </div>
          </div>

          <div className="form-step">
            <h3 className="step-title">
              Step 2 – Enter a few details about the product
            </h3>
            <div className="form-group">
              <textarea
                value={productDetails}
                onChange={(e) => setProductDetails(e.target.value)}
                placeholder="Please describe your product, including its key features, benefits, and any other relevant details."
                rows={5}
                className="form-control"
              />
            </div>
          </div>

          <div className="form-step">
            <h3 className="step-title">
              Step 3 – Advanced Settings (optional)
            </h3>
            <div className="form-group">
              <label htmlFor="language" className="form-label">
                Select Language
              </label>
              <div className="select-wrapper">
                <select
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="form-select"
                >
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="select-arrow"></div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className={`generate-btn ${generating ? "loading" : ""}`}
              onClick={generateDescription}
              disabled={
                generating ||
                !productName.trim() ||
                !productDetails.trim() ||
                credits < 1 ||
                apiKeys.length === 0
              }
            >
              {generating ? (
                <>
                  <span className="btn-spinner"></span>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <span>Create Content</span>
                </>
              )}
            </button>
            <button type="button" className="reset-btn" onClick={resetForm}>
              Reset
            </button>
          </div>
        </div>

        {/* Right Side - Result Editor */}
        <div className="editor-section">
          <div className="editor-header">
            <h2 className="editor-title">Your Result</h2>
            <div className="header-actions">
              <button
                className="download-all-btn"
                onClick={downloadAllContent}
                disabled={!productDescription && featureHighlights.length === 0}
              >
                <Download size={16} />
                <span>Download All</span>
              </button>
            </div>
          </div>

          <div className="editor-container">
            {generating ? (
              <div className="editor-loading">
                <div className="ai-generating-animation">
                  <div className="ai-icon">
                    <div className="ai-icon-inner"></div>
                  </div>
                  <div className="ai-text">
                    <p>AI is generating your product content package...</p>
                    <div className="ai-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="generated-content">
                {/* Product Descriptions Section */}
                {productDescription && (
                  <div className="content-section">
                    <div className="section-header">
                      <div className="section-title-wrapper">
                        <FileText size={18} className="section-icon" />
                        <h3 className="section-title">Product Descriptions</h3>
                      </div>
                      <button
                        className="copy-btn"
                        onClick={() =>
                          copyToClipboard(
                            productDescription,
                            "Product Descriptions"
                          )
                        }
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <div className="section-content product-description-content">
                      {productDescription
                        .split("\n\n")
                        .map((paragraph, index) =>
                          paragraph.trim() !== "" ? (
                            <p key={index}>{paragraph}</p>
                          ) : null
                        )}
                    </div>
                  </div>
                )}

                {/* Feature Highlights Section */}
                {featureHighlights && featureHighlights.length > 0 && (
                  <div className="content-section">
                    <div className="section-header">
                      <div className="section-title-wrapper">
                        <AlignLeft size={18} className="section-icon" />
                        <h3 className="section-title">Feature Highlights</h3>
                      </div>
                      <button
                        className="copy-btn"
                        onClick={() =>
                          copyToClipboard(
                            featureHighlights
                              .map((item) => `• ${item}`)
                              .join("\n"),
                            "Feature Highlights"
                          )
                        }
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <div className="section-content">
                      <ul className="feature-list">
                        {featureHighlights.map((highlight, index) => (
                          <li key={index}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Specification Details Section */}
                {specificationDetails && specificationDetails.length > 0 && (
                  <div className="content-section">
                    <div className="section-header">
                      <div className="section-title-wrapper">
                        <FileText size={18} className="section-icon" />
                        <h3 className="section-title">Specification Details</h3>
                      </div>
                      <button
                        className="copy-btn"
                        onClick={() =>
                          copyToClipboard(
                            specificationDetails
                              .map((item) => `• ${item}`)
                              .join("\n"),
                            "Specification Details"
                          )
                        }
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <div className="section-content">
                      <ul className="spec-list">
                        {specificationDetails.map((spec, index) => (
                          <li key={index}>{spec}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* SEO-Optimized Content Section */}
                {seoOptimizedContent &&
                  ((seoOptimizedContent.titleOptions &&
                    seoOptimizedContent.titleOptions.length > 0) ||
                    (seoOptimizedContent.seoBulletPoints &&
                      seoOptimizedContent.seoBulletPoints.length > 0) ||
                    seoOptimizedContent.seoDescription) && (
                    <div className="content-section">
                      <div className="section-header">
                        <div className="section-title-wrapper">
                          <FileText size={18} className="section-icon" />
                          <h3 className="section-title">
                            SEO-Optimized Content
                          </h3>
                        </div>
                        <button
                          className="copy-btn"
                          onClick={() => {
                            let content = "";
                            if (
                              seoOptimizedContent.titleOptions &&
                              seoOptimizedContent.titleOptions.length > 0
                            ) {
                              content +=
                                "Optimized Titles:\n" +
                                seoOptimizedContent.titleOptions.join("\n") +
                                "\n\n";
                            }
                            if (
                              seoOptimizedContent.seoBulletPoints &&
                              seoOptimizedContent.seoBulletPoints.length > 0
                            ) {
                              content +=
                                "SEO Bullet Points:\n" +
                                seoOptimizedContent.seoBulletPoints
                                  .map((item) => `• ${item}`)
                                  .join("\n") +
                                "\n\n";
                            }
                            if (seoOptimizedContent.seoDescription) {
                              content +=
                                "SEO Description:\n" +
                                seoOptimizedContent.seoDescription;
                            }
                            copyToClipboard(content, "SEO-Optimized Content");
                          }}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                      <div className="section-content">
                        {/* Optimized Titles */}
                        {seoOptimizedContent.titleOptions &&
                          seoOptimizedContent.titleOptions.length > 0 && (
                            <div className="subsection">
                              <h4 className="subsection-title">
                                Optimized Titles
                              </h4>
                              <ul className="title-list">
                                {seoOptimizedContent.titleOptions.map(
                                  (title, index) => (
                                    <li key={index}>{title}</li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}

                        {/* SEO Bullet Points */}
                        {seoOptimizedContent.seoBulletPoints &&
                          seoOptimizedContent.seoBulletPoints.length > 0 && (
                            <div className="subsection">
                              <h4 className="subsection-title">
                                SEO Bullet Points
                              </h4>
                              <ul className="seo-bullet-list">
                                {seoOptimizedContent.seoBulletPoints.map(
                                  (point, index) => (
                                    <li key={index}>{point}</li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}

                        {/* SEO Description */}
                        {seoOptimizedContent.seoDescription && (
                          <div className="subsection">
                            <h4 className="subsection-title">
                              SEO Description
                            </h4>
                            <p>{seoOptimizedContent.seoDescription}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Meta Tags Section - Always show after generation */}
                {!generating &&
                  (productDescription ||
                    featureHighlights.length > 0 ||
                    specificationDetails.length > 0 ||
                    (seoOptimizedContent.titleOptions &&
                      seoOptimizedContent.titleOptions.length > 0) ||
                    (seoOptimizedContent.seoBulletPoints &&
                      seoOptimizedContent.seoBulletPoints.length > 0) ||
                    seoOptimizedContent.seoDescription ||
                    slug ||
                    (productTags && productTags.length > 0)) && (
                    <div className="content-section meta-tags-section">
                      <div className="section-header">
                        <div className="section-title-wrapper">
                          <Type size={18} className="section-icon" />
                          <h3 className="section-title">Meta Tags</h3>
                        </div>
                        <div className="meta-actions"></div>
                      </div>
                      <div className="section-content">
                        {/* Meta Title */}
                        <div className="meta-item">
                          <div className="meta-header">
                            <h4 className="meta-label">Meta Title</h4>
                            <div className="meta-header-actions">
                              <span
                                className={`char-count ${
                                  metaTags.title && metaTags.title.length > 60
                                    ? "warning"
                                    : ""
                                }`}
                              >
                                {metaTags.title ? metaTags.title.length : 0}/60
                                characters
                              </span>
                              <button
                                className="copy-meta-btn"
                                onClick={() =>
                                  copyToClipboard(
                                    metaTags.title || "",
                                    "Meta Title"
                                  )
                                }
                                title="Copy Meta Title"
                              >
                                <Copy size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="meta-content">
                            <div className="meta-text-container">
                              <p className="meta-title">{metaTags.title}</p>
                              <div className="meta-preview">
                                <div className="browser-tab">
                                  <div className="browser-tab-favicon"></div>
                                  <div className="browser-tab-title">
                                    {metaTags.title
                                      ? metaTags.title.length > 60
                                        ? metaTags.title.substring(0, 60) +
                                          "..."
                                        : metaTags.title
                                      : "Product Title"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Meta Description */}
                        <div className="meta-item">
                          <div className="meta-header">
                            <h4 className="meta-label">Meta Description</h4>
                            <div className="meta-header-actions">
                              <span
                                className={`char-count ${
                                  metaTags.description &&
                                  metaTags.description.length > 160
                                    ? "warning"
                                    : ""
                                }`}
                              >
                                {metaTags.description
                                  ? metaTags.description.length
                                  : 0}
                                /160 characters
                              </span>
                              <button
                                className="copy-meta-btn"
                                onClick={() =>
                                  copyToClipboard(
                                    metaTags.description || "",
                                    "Meta Description"
                                  )
                                }
                                title="Copy Meta Description"
                              >
                                <Copy size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="meta-content">
                            <div className="meta-text-container">
                              <p className="meta-description">
                                {metaTags.description}
                              </p>
                              <div className="meta-preview">
                                <div className="search-result">
                                  <div className="search-result-title">
                                    {metaTags.title || "Product Title"}
                                  </div>
                                  <div className="search-result-url">
                                    example.com/products/
                                    {slug || "product-slug"}
                                  </div>
                                  <div className="search-result-description">
                                    {metaTags.description
                                      ? metaTags.description.length > 160
                                        ? metaTags.description.substring(
                                            0,
                                            160
                                          ) + "..."
                                        : metaTags.description
                                      : "Product description will appear here..."}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Slug Section */}
                {slug && (
                  <div className="content-section">
                    <div className="section-header">
                      <div className="section-title-wrapper">
                        <Hash size={18} className="section-icon" />
                        <h3 className="section-title">Slug</h3>
                      </div>
                      <button
                        className="copy-btn"
                        onClick={() => copyToClipboard(slug, "Slug")}
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <div className="section-content">
                      <div className="slug-container">
                        <p className="slug-text">{slug}</p>
                        <div className="slug-preview">
                          <span className="url-base">
                            example.com/products/
                          </span>
                          <span className="slug-value">{slug}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Products Tags Section */}
                {productTags && productTags.length > 0 && (
                  <div className="content-section">
                    <div className="section-header">
                      <div className="section-title-wrapper">
                        <Tag size={18} className="section-icon" />
                        <h3 className="section-title">Products Tags</h3>
                      </div>
                      <div className="meta-actions">
                        <button
                          className="copy-btn"
                          onClick={() => {
                            const tagsText = productTags
                              .map((tag) => `#${tag}`)
                              .join(", ");
                            copyToClipboard(tagsText, "Products Tags");
                          }}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="section-content">
                      <div className="tags-container">
                        <div className="tags-list">
                          {productTags.map((tag, index) => (
                            <span key={index} className="tag-item">
                              #{tag}
                            </span>
                          ))}
                        </div>
                        <div className="tags-preview">
                          <p className="tags-preview-text">
                            {productTags.map((tag) => `#${tag}`).join(", ")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      <style jsx>{`
        /* Global Styles */
        .product-description-generator {
          font-family: "Inter", "Poppins", sans-serif;
          background-color: #ffffff;
          color: #333;
          min-height: 100vh;
          padding: 20px;
        }

        /* Top Navigation */
        .top-navigation {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding: 15px 20px;
          background-color: #fff;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }

        .back-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: none;
          border: none;
          color: #4a5568;
          font-weight: 500;
          cursor: pointer;
          transition: color 0.2s;
        }

        .back-btn:hover {
          color: #3182ce;
        }

        .credits-display {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .credits-info {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 15px;
          background-color: #f7fafc;
          border-radius: 20px;
        }

        .credits-label {
          font-weight: 500;
          color: #4a5568;
        }

        .credits-value {
          font-weight: 700;
          color: #3182ce;
        }

        .buy-credits-btn {
          padding: 8px 15px;
          background-color: #3182ce;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .buy-credits-btn:hover {
          background-color: #2c5aa0;
        }

        /* Section Header */
        .section-header {
          margin-bottom: 30px;
          text-align: center;
        }

        .section-title {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 28px;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 10px;
        }

        .info-icon {
          color: #718096;
          cursor: help;
        }

        .section-subtext {
          max-width: 700px;
          margin: 0 auto;
          color: #4a5568;
          font-size: 16px;
          line-height: 1.5;
        }

        /* Messages */
        .error-message {
          padding: 12px 20px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-weight: 500;
          background-color: #fed7d7;
          color: #c53030;
        }

        /* Main Content */
        .main-content {
          display: flex;
          gap: 30px;
        }

        /* Form Section */
        .form-section {
          flex: 1;
          background-color: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          padding: 25px;
        }

        .form-step {
          margin-bottom: 25px;
        }

        .step-title {
          font-size: 18px;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 12px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-control,
        .form-select {
          width: 100%;
          padding: 12px 15px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 15px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #3182ce;
          box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
          outline: none;
        }

        .select-wrapper {
          position: relative;
        }

        .select-arrow {
          position: absolute;
          top: 50%;
          right: 15px;
          transform: translateY(-50%);
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 5px solid #718096;
          pointer-events: none;
        }

        .form-actions {
          display: flex;
          gap: 15px;
          margin-top: 30px;
        }

        .generate-btn {
          flex: 1;
          padding: 14px 20px;
          background: linear-gradient(135deg, #805ad5 0%, #3182ce 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .generate-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(49, 130, 206, 0.3);
        }

        .generate-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .reset-btn {
          padding: 14px 20px;
          background-color: #f7fafc;
          color: #4a5568;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s, color 0.2s;
        }

        .reset-btn:hover {
          background-color: #edf2f7;
          color: #2d3748;
        }

        /* Editor Section */
        .editor-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          background-color: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 25px;
          border-bottom: 1px solid #e2e8f0;
        }

        .editor-title {
          font-size: 20px;
          font-weight: 600;
          color: #2d3748;
        }

        .header-actions {
          display: flex;
          gap: 10px;
        }

        .download-all-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background-color: #4299e1;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .download-all-btn:hover:not(:disabled) {
          background-color: #3182ce;
        }

        .download-all-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .save-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background-color: #38a169;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .save-btn:hover:not(:disabled) {
          background-color: #2f855a;
        }

        .save-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .editor-container {
          flex: 1;
          min-height: 500px;
          overflow-y: auto;
        }

        .editor-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 40px;
        }

        .ai-generating-animation {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }

        .ai-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #805ad5 0%, #3182ce 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          animation: pulse 2s infinite;
        }

        .ai-icon-inner {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: white;
        }

        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(49, 130, 206, 0.4);
          }
          70% {
            box-shadow: 0 0 0 15px rgba(49, 130, 206, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(49, 130, 206, 0);
          }
        }

        .ai-text {
          text-align: center;
        }

        .ai-text p {
          font-size: 18px;
          color: #4a5568;
          margin-bottom: 15px;
        }

        .ai-dots {
          display: flex;
          gap: 5px;
        }

        .ai-dots span {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #3182ce;
          animation: bounce 1.4s infinite ease-in-out both;
        }

        .ai-dots span:nth-child(1) {
          animation-delay: -0.32s;
        }

        .ai-dots span:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes bounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }

        /* Generated Content */
        .generated-content {
          padding: 20px;
        }

        .content-section {
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e2e8f0;
        }

        .content-section:last-child {
          border-bottom: none;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .section-title-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .section-icon {
          color: #4a5568;
        }

        .section-title {
          font-size: 18px;
          font-weight: 600;
          color: #2d3748;
          margin: 0;
        }

        .copy-btn,
        .download-btn {
          background: none;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          color: #4a5568;
          cursor: pointer;
          padding: 6px 10px;
          transition: background-color 0.2s, color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .copy-btn:hover,
        .download-btn:hover {
          background-color: #f7fafc;
          color: #3182ce;
        }

        .section-content {
          color: #4a5568;
          line-height: 1.6;
        }

        /* Product Description Content */
        .product-description-content {
          background-color: #f7fafc;
          border-radius: 8px;
          padding: 16px;
          border-left: 3px solid #3182ce;
        }

        .product-description-content p {
          margin-bottom: 1em;
          line-height: 1.7;
        }

        .product-description-content p:last-child {
          margin-bottom: 0;
        }

        .subsection {
          margin-bottom: 20px;
        }

        .subsection:last-child {
          margin-bottom: 0;
        }

        .subsection-title {
          font-size: 16px;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 10px;
        }

        /* Lists */
        .feature-list,
        .spec-list,
        .seo-bullet-list {
          padding-left: 20px;
          margin: 0;
        }

        .feature-list li,
        .spec-list li,
        .seo-bullet-list li {
          margin-bottom: 8px;
          position: relative;
          padding-left: 8px;
        }

        .feature-list li:last-child,
        .spec-list li:last-child,
        .seo-bullet-list li:last-child {
          margin-bottom: 0;
        }

        .feature-list li:before,
        .spec-list li:before,
        .seo-bullet-list li:before {
          content: "•";
          position: absolute;
          left: -15px;
          color: #3182ce;
        }

        .title-list {
          list-style-type: decimal;
          padding-left: 20px;
          margin: 0;
        }

        .title-list li {
          margin-bottom: 8px;
        }

        .title-list li:last-child {
          margin-bottom: 0;
        }

        /* Enhanced Meta Tags Section */
        .meta-tags-section {
          background-color: #f8fafc;
          border-radius: 8px;
          padding: 5px;
        }

        .meta-actions {
          display: flex;
          gap: 8px;
        }

        .meta-item {
          margin-bottom: 20px;
        }

        .meta-item:last-child {
          margin-bottom: 0;
        }

        .meta-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .meta-header-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .meta-label {
          font-size: 16px;
          font-weight: 600;
          color: #2d3748;
          margin: 0;
        }

        .char-count {
          font-size: 12px;
          color: #718096;
          background-color: #edf2f7;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .char-count.warning {
          color: #e53e3e;
          background-color: #fed7d7;
        }

        .copy-meta-btn {
          background: none;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          color: #4a5568;
          cursor: pointer;
          padding: 4px 8px;
          transition: background-color 0.2s, color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .copy-meta-btn:hover {
          background-color: #f7fafc;
          color: #3182ce;
        }

        .meta-content {
          background-color: #fff;
          border-radius: 6px;
          padding: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .meta-text-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .meta-title,
        .meta-description {
          margin: 0;
          padding: 8px 12px;
          background-color: #f7fafc;
          border-radius: 4px;
          border-left: 3px solid #3182ce;
          min-height: 40px;
        }

        .meta-preview {
          margin-top: 0;
        }

        /* Browser Tab Preview */
        .browser-tab {
          display: flex;
          align-items: center;
          background-color: #e2e8f0;
          border-radius: 4px 4px 0 0;
          padding: 8px 12px;
          max-width: 400px;
        }

        .browser-tab-favicon {
          width: 16px;
          height: 16px;
          background-color: #3182ce;
          border-radius: 2px;
          margin-right: 8px;
        }

        .browser-tab-title {
          font-size: 14px;
          color: #2d3748;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Search Result Preview */
        .search-result {
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 12px;
          max-width: 500px;
        }

        .search-result-title {
          font-size: 16px;
          color: #1a0dab;
          font-weight: 600;
          margin-bottom: 4px;
          cursor: pointer;
        }

        .search-result-title:hover {
          text-decoration: underline;
        }

        .search-result-url {
          font-size: 14px;
          color: #006621;
          margin-bottom: 4px;
        }

        .search-result-description {
          font-size: 14px;
          color: #4a5568;
          line-height: 1.4;
        }

        /* Slug Section */
        .slug-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .slug-text {
          background-color: #f7fafc;
          padding: 10px 15px;
          border-radius: 6px;
          margin: 0;
          font-family: monospace;
          word-break: break-all;
        }

        .slug-preview {
          display: flex;
          align-items: center;
          background-color: #f7fafc;
          padding: 8px 12px;
          border-radius: 6px;
          font-family: monospace;
          font-size: 14px;
        }

        .url-base {
          color: #718096;
        }

        .slug-value {
          color: #3182ce;
          font-weight: 600;
        }

        /* Products Tags Section */
        .tags-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .tags-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 12px;
        }

        .tag-item {
          display: inline-block;
          padding: 6px 12px;
          background-color: #ebf4ff;
          color: #3182ce;
          border-radius: 16px;
          font-size: 14px;
          font-weight: 500;
        }

        .tags-preview {
          background-color: #f7fafc;
          padding: 10px 15px;
          border-radius: 6px;
          margin: 0;
        }

        .tags-preview-text {
          color: #4a5568;
          font-family: monospace;
          font-size: 14px;
        }

        /* Loading Spinner */
        .btn-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s ease-in-out infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Auth Container */
        .auth-container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background-color: #f7fafc;
        }

        .auth-card {
          background-color: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          padding: 40px;
          max-width: 500px;
          width: 100%;
          text-align: center;
        }

        .auth-card h2 {
          font-size: 24px;
          margin-bottom: 25px;
          color: #2d3748;
        }

        .login-btn {
          padding: 12px 24px;
          background-color: #3182ce;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .login-btn:hover {
          background-color: #2c5aa0;
        }

        /* Loading Container */
        .loading-container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background-color: #f7fafc;
        }

        .loading-spinner-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(49, 130, 206, 0.2);
          border-radius: 50%;
          border-top-color: #3182ce;
          animation: spin 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ProductDescriptionGenerator;
